import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useNavigate } from "react-router-dom";
import Title from "../components/header/Title";
import AuthContext from "../store/auth-context";
import LocaleContext from "../store/locale-context";

const SETTINGS_KEY = "muradi_backoffice_settings";

type Prefs = {
  compactTables: boolean;
  orderAlerts: boolean;
  refundAlerts: boolean;
  lowStockAlerts: boolean;
  confirmDeletes: boolean;
};

const DEFAULT_PREFS: Prefs = {
  compactTables: false,
  orderAlerts: true,
  refundAlerts: true,
  lowStockAlerts: true,
  confirmDeletes: true,
};

const loadPrefs = (): Prefs => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
};

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ icon, title, description, children }) => (
  <Box
    sx={{
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: "14px",
      boxShadow: "0 1px 3px rgba(16,24,40,0.05)",
      overflow: "hidden",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, px: 3, py: 2.25 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          bgcolor: "primary.light",
          color: "primary.main",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
    <Divider />
    <Box>{children}</Box>
  </Box>
);

const Row: React.FC<{
  label: string;
  hint?: string;
  control: React.ReactNode;
  last?: boolean;
}> = ({ label, hint, control, last }) => (
  <>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 3,
        py: 1.75,
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexShrink: 0 }}>{control}</Box>
    </Box>
    {!last && <Divider sx={{ mx: 3 }} />}
  </>
);

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const { language, languages, setLanguage, country, countries, setCountry } =
    useContext(LocaleContext);
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [saved, setSaved] = useState(false);

  const updatePref = (key: keyof Prefs) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = { ...prefs, [key]: e.target.checked };
    setPrefs(next);
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
      setSaved(true);
    } catch {
      /* storage unavailable – keep the in-memory value */
    }
  };

  const handleCountry = (code: string) => {
    if (code === country) return;
    setCountry(code);
    // Same behaviour as the header picker: the session is country-scoped, so sign in again.
    logout();
    navigate("/login", { replace: true });
  };

  const handleResetPassword = () => {
    logout();
    navigate("/forgot-password", { replace: true });
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <React.Fragment>
      <Title title="Settings" subtitle="Manage language, preferences and account security" />

      <Box sx={{ display: "grid", gap: 2.5, maxWidth: 860 }}>
        <Section
          icon={<TranslateRoundedIcon />}
          title="Language & region"
          description="Choose how Seller Center is displayed"
        >
          <Row
            label="Display language"
            hint="Applies immediately"
            control={
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select value={language} onChange={(e) => setLanguage(String(e.target.value))}>
                  {languages.map((l) => (
                    <MenuItem key={l.code} value={l.code}>
                      {l.nativeName !== l.name ? `${l.nativeName} (${l.name})` : l.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />
          <Row
            last
            label="Country / currency"
            hint="Changing country signs you out so data reloads for that market"
            control={
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select value={country} onChange={(e) => handleCountry(String(e.target.value))}>
                  {countries.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.name}
                      {c.currencyCode ? ` · ${c.currencyCode}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />
        </Section>

        <Section
          icon={<TuneRoundedIcon />}
          title="Preferences"
          description="Saved on this device"
        >
          <Row
            label="Compact tables"
            hint="Show more rows on screen"
            control={<Switch checked={prefs.compactTables} onChange={updatePref("compactTables")} />}
          />
          <Row
            label="Confirm before deleting"
            hint="Ask for confirmation on destructive actions"
            control={<Switch checked={prefs.confirmDeletes} onChange={updatePref("confirmDeletes")} />}
          />
          <Row
            label="New order alerts"
            control={<Switch checked={prefs.orderAlerts} onChange={updatePref("orderAlerts")} />}
          />
          <Row
            label="Refund request alerts"
            control={<Switch checked={prefs.refundAlerts} onChange={updatePref("refundAlerts")} />}
          />
          <Row
            last
            label="Low stock alerts"
            control={<Switch checked={prefs.lowStockAlerts} onChange={updatePref("lowStockAlerts")} />}
          />
        </Section>

        <Section
          icon={<LockOutlinedIcon />}
          title="Security"
          description="Keep your account protected"
        >
          <Row
            last
            label="Password"
            hint="You will be signed out and sent to the password reset flow"
            control={
              <Button variant="outlined" onClick={handleResetPassword}>
                Reset password
              </Button>
            }
          />
        </Section>

        <Section
          icon={<LogoutRoundedIcon />}
          title="Session"
          description="Sign out of this device"
        >
          <Row
            last
            label="Log out"
            hint="End your current session"
            control={
              <Button variant="contained" color="error" onClick={handleLogout}>
                Log out
              </Button>
            }
          />
        </Section>
      </Box>

      <Snackbar open={saved} autoHideDuration={1800} onClose={() => setSaved(false)}>
        <Alert severity="success" onClose={() => setSaved(false)}>
          Preference saved
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default Settings;
